import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    ViewEncapsulation,
    ViewChildren,
    QueryList,
    effect,
} from '@angular/core';
import { FiQuizHelperService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-helper.service';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import { IQuizQuestion } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz.d';
import { Animations } from '@fi-app/shared/utils/animations';
import { FiQuizQuestionCardComponent } from './quiz-question-card/quiz-question-card.component';
import { FiQuizEditorStoreService } from '../../fi-quiz-editor-store.service';

@Component({
    selector: 'fi-quiz-added-questions-list',
    templateUrl: './quiz-added-questions-list.component.html',
    styleUrls: ['./quiz-added-questions-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-quiz-added-questions-list' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiQuizAddedQuestionsListComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiQuizAddedQuestionsListComponent,
            multi: true,
        },
    ],
    animations: [Animations.fadeHeight()],
})
export class FiQuizAddedQuestionsListComponent extends ControlValueAccessorValidatorBase<IQuizQuestion[]> {
    private fiQuizHelperService = inject(FiQuizHelperService);
    private fiQuizEditorStoreService = inject(FiQuizEditorStoreService);
    protected dropListId = this.fiQuizHelperService.quizAddedQuestionsDropListId;

    @ViewChildren(FiQuizQuestionCardComponent) questionCards: QueryList<FiQuizQuestionCardComponent>;
    @ViewChildren(FiQuizQuestionCardComponent, { read: ElementRef })
    private questionCardElements: QueryList<ElementRef<HTMLElement>>;

    private readonly scrollToQuestionCardEffect = effect(() => {
        const scrollToQuestionCardIndex = this.fiQuizEditorStoreService.scrollToQuestionCard();
        if (scrollToQuestionCardIndex === undefined) {
            return;
        }
        this.scrollQuestionCardIntoView(scrollToQuestionCardIndex);
    });

    protected onElementDrop($event: CdkDragDrop<any[]>): void {
        if ($event.previousContainer.id === this.fiQuizHelperService.quizAvailableQuestionTypesDropListId) {
            this.fiQuizHelperService.insertNewQuizQuestionAtPosition($event.item.data.type, $event.currentIndex);
            this.scrollQuestionCardIntoView($event.currentIndex);
            return;
        }
        this.fiQuizHelperService.onQuizQuestionsPositionChange($event.previousIndex, $event.currentIndex);
        this.scrollQuestionCardIntoView($event.currentIndex);
    }

    private scrollQuestionCardIntoView(questionIndex: number): void {
        setTimeout(() => {
            const questionCardElement = this.questionCardElements.get(questionIndex)?.nativeElement;
            questionCardElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    public doValidate(control: AbstractControl): ValidationErrors | null {
        return {
            'missing-questions': !this.ngModel?.length,
        };
    }
}
